import React, { useState } from 'react';
import { Handle, Position } from '@xyflow/react';
import { Trash2, AlertTriangle } from 'lucide-react';
import { NODE_META, NODE_HANDLES, COLOR_CLASSES, nodeWidth } from './nodeTypes';

const HANDLE_DOT_HEX = {
  out: '#2dd4bf', open: '#34d399', closed: '#fbbf24',
  has: '#34d399', lacks: '#94a3b8', known: '#34d399', unknown: '#94a3b8',
  no_answer: '#fb7185', timeout: '#fbbf24',
};
export function dotHexFor(handleId) {
  if (HANDLE_DOT_HEX[handleId]) return HANDLE_DOT_HEX[handleId];
  if (handleId && handleId.startsWith('key_')) return '#38bdf8';
  return '#2dd4bf';
}

const PULSE = {
  matched: 'shadow-[0_0_0_3px_rgba(52,211,153,0.55),0_0_24px_rgba(52,211,153,0.6)]',
  fallback: 'shadow-[0_0_0_3px_rgba(251,191,36,0.55),0_0_24px_rgba(251,191,36,0.6)]',
  visited: 'shadow-[0_0_0_2px_rgba(45,212,191,0.30)]',
  skipped: 'shadow-[0_0_0_3px_rgba(148,163,184,0.45),0_0_18px_rgba(148,163,184,0.45)]',
  error: 'shadow-[0_0_0_3px_rgba(244,63,94,0.55),0_0_24px_rgba(244,63,94,0.6)]',
};
const pulseKey = (p) => (['ok', 'prompt'].includes(p) ? 'matched' : ['no_answer', 'timeout'].includes(p) ? 'fallback' : p);

/* Output strip: labels and handles share the same % positions so wires
   always leave exactly under their label. */
function OutputStrip({ outputs, unwired }) {
  return (
    <div className="relative h-9 border-t" style={{ borderColor: 'var(--vox-node-border)' }}>
      {outputs.map((h, idx) => {
        const hex = dotHexFor(h.id);
        const left = `${((idx + 0.5) / outputs.length) * 100}%`;
        return (
          <React.Fragment key={h.id}>
            <div className="absolute top-1.5 -translate-x-1/2 flex items-center gap-1 px-1" style={{ left, maxWidth: `${100 / outputs.length}%` }} title={h.label}>
              <span className="text-[9px] font-bold uppercase tracking-wider truncate" style={{ color: hex }}>{h.label}</span>
              {unwired.has(h.id) && (
                <span title="Not connected yet. Calls taking this path use the flow failsafe." className="inline-flex text-amber-500 dark:text-amber-300" data-testid={`callflow-unwired-${h.id}`}>
                  <AlertTriangle size={8} />
                </span>
              )}
            </div>
            <Handle type="source" position={Position.Bottom} id={h.id} className="vox-handle-out"
              style={{ backgroundColor: hex, left }} data-testid={`callflow-handle-${h.id}`} />
          </React.Fragment>
        );
      })}
    </div>
  );
}

function CallFlowNode({ data, selected, type, id }) {
  const [hover, setHover] = useState(false);
  const meta = NODE_META[type] || { label: type, icon: () => null, color: 'sky' };
  const Icon = meta.icon;
  const c = COLOR_CLASSES[meta.color] || COLOR_CLASSES.sky;
  const handlesDef = NODE_HANDLES[type];
  const outputs = typeof handlesDef === 'function' ? handlesDef({ data }) : handlesDef || [];
  const subtitle = renderSubtitle(type, data);
  const unwired = data?._unwiredHandles instanceof Set ? data._unwiredHandles : new Set();

  const onDeleteClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    data?._onDelete?.(id);
  };

  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className={`relative rounded-xl border transition-[box-shadow,border-color] duration-200 ${selected ? 'shadow-[0_0_0_2px_rgba(45,212,191,0.35)]' : ''} ${PULSE[pulseKey(data?._pulse)] || ''}`}
      style={{ width: nodeWidth(type, data), background: 'var(--vox-node-bg)', borderColor: selected ? '#2dd4bf' : 'var(--vox-node-border)' }}
      data-testid={`callflow-node-${type}-${id}`}
    >
      {type !== 'start' && <Handle type="target" position={Position.Top} id="in" className="vox-handle-in" />}

      {type !== 'start' && hover && (
        <button onClick={onDeleteClick}
          className="absolute -top-2 -right-2 z-20 w-6 h-6 rounded-full bg-rose-500 hover:bg-rose-600 text-white flex items-center justify-center shadow-lg border-2"
          style={{ borderColor: 'var(--vox-handle-ring)' }} title="Delete step" data-testid={`callflow-node-delete-${id}`}>
          <Trash2 size={11} />
        </button>
      )}

      <div className="flex items-start gap-3 px-3.5 py-3">
        <div className={`w-8 h-8 rounded-lg ${c.bg} ${c.text} flex items-center justify-center flex-shrink-0`}>
          {Icon ? <Icon size={15} /> : null}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <div className="text-[13px] font-semibold truncate" style={{ color: 'var(--vox-text-primary)' }}>{data?.label || (type === 'department' && data?.name) || meta.label}</div>
            {needsSetup(type, data) && <span className="w-1.5 h-1.5 rounded-full bg-amber-400 flex-shrink-0" title="Needs setup" data-testid={`callflow-node-warning-${id}`} />}
          </div>
          {(data?.label || (type === 'department' && data?.name)) && <div className={`text-[9px] font-bold uppercase tracking-wider ${c.text}`}>{meta.label}</div>}
          {subtitle && <div className="text-[11px] mt-0.5 whitespace-pre-line leading-snug" style={{ color: 'var(--vox-text-secondary)' }}>{subtitle}</div>}
        </div>
      </div>

      {outputs.length > 0 && <OutputStrip outputs={outputs} unwired={unwired} />}
      {outputs.length === 0 && type !== 'hangup' && (
        <Handle type="source" position={Position.Bottom} id="out" className="vox-handle-out" style={{ backgroundColor: '#2dd4bf' }} />
      )}
    </div>
  );
}

function renderSubtitle(type, data) {
  if (!data) return '';
  switch (type) {
    case 'start': {
      const who = data.agent_id ? `AI answers first: ${data.agent_name || 'IVR agent'}` : (data._phoneNumber || 'Pick an IVR agent');
      return `${who}\n${data.ai_cap ? `Max ${data.ai_cap} AI calls at once` : 'Full line limit'}`;
    }
    case 'menu': {
      const navBits = [];
      if (data.repeat_enabled) navBits.push(`${data.repeat_key || '9'} ↻ repeat`);
      if (data.main_menu_enabled) navBits.push(`${data.main_menu_key || '0'} ↑ main`);
      const prompt = data.prompt ? truncate(data.prompt, 70) : 'No prompt set';
      return navBits.length ? `${prompt}\n${navBits.join(' · ')}` : prompt;
    }
    case 'tag_filter': return data.tag ? `Tag: ${data.tag}` : 'No tag set';
    case 'contact_filter': return 'Known vs unknown';
    case 'hangup': return data.farewell ? `"${truncate(data.farewell, 50)}"` : 'No farewell';
    case 'department': {
      const n = (data.numbers || []).filter((x) => (x.number || '').trim()).length;
      return n ? `${n} number${n > 1 ? 's' : ''} · ${({ simultaneous: 'ring all', sequential: 'in order', round_robin: 'round-robin' })[data.strategy] || 'ring all'}${data.hours_enabled ? ' · own hours' : ''}` : 'No numbers yet';
    }
    case 'queue': {
      const hold = Number(data.min_hold_sec) || 0;
      return `${hold ? `${hold}s hold music · ` : ''}max ${data.max_wait_min || 20} min${data.callback_offer !== false ? ' · callback' : ''}`;
    }
    default: return '';
  }
}
function needsSetup(type, d = {}) {
  switch (type) {
    case 'start': return !d.agent_id && !d._phoneNumber;
    case 'menu': return !(d.options || []).some((o) => (o.label || '').trim());
    case 'tag_filter': return !d.tag;
    case 'department': return !(d.numbers || []).some((x) => (x.number || '').trim());
    default: return false;
  }
}
function truncate(s, n) { return (s && s.length > n) ? s.slice(0, n - 1) + '…' : (s || ''); }

export default React.memo(CallFlowNode);
